import { createFileRoute, Link } from '@tanstack/react-router'
import { Badge } from '@workspace/ui/components/badge'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { QueryGuard } from '@/components/query-guard'
import { useAdminAutomatedEmailByIdQuery } from '@/hooks/api'
import { formatDateTime } from '@/utils/format'
import { getEmailTypeLabel } from './email-type-labels'

export const Route = createFileRoute('/_protected/admin/emails/$id')({
  component: AdminEmailDetailPage,
})

function AdminEmailDetailPage() {
  const { id } = Route.useParams()
  const emailQuery = useAdminAutomatedEmailByIdQuery(id)

  return (
    <QueryGuard query={emailQuery} errorMessage="Failed to load email">
      {(email) => (
        <>
          <nav className="border-b border-border">
            <div className="flex items-center gap-2 py-3">
              <Link
                to="/admin/emails"
                className="flex items-center gap-1 text-sm font-medium text-[#f97316] hover:underline"
              >
                <ArrowLeft className="size-4" />
                Emails
              </Link>
              <ChevronRight className="size-4 text-muted-foreground" />
              <span className="text-sm font-medium">{email.subject}</span>
            </div>
          </nav>

          <div className="space-y-6 py-6">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Type">
                <Badge variant="secondary" size="sm">
                  {getEmailTypeLabel(email.emailType)}
                </Badge>
              </Field>
              <Field label="Sent">
                <span className="text-sm">{formatDateTime(email.createdAt)}</span>
              </Field>
              <Field label="Recipient">
                <Link
                  to="/admin/users/$id"
                  params={{ id: email.user.id }}
                  className="text-sm text-[#f97316] hover:underline"
                >
                  {email.user.name || email.recipientEmail}
                </Link>
                <span className="ml-2 text-sm text-muted-foreground">{email.recipientEmail}</span>
              </Field>
              <Field label="External ID">
                <span className="font-mono text-xs text-muted-foreground">
                  {email.externalId ?? '—'}
                </span>
              </Field>
              {email.contextId && (
                <Field label="Context ID">
                  <span className="font-mono text-xs text-muted-foreground">{email.contextId}</span>
                </Field>
              )}
            </div>

            <div>
              <h3 className="mb-2 text-sm font-medium text-muted-foreground">Subject</h3>
              <p className="text-sm">{email.subject}</p>
            </div>

            <div>
              <h3 className="mb-2 text-sm font-medium text-muted-foreground">Body</h3>
              {isHtml(email.body) ? (
                <iframe
                  srcDoc={email.body}
                  title="Email preview"
                  className="w-full rounded-md border border-border"
                  style={{ height: 600 }}
                  sandbox=""
                />
              ) : (
                <pre className="whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-4 text-sm leading-relaxed">
                  {email.body}
                </pre>
              )}
            </div>
          </div>
        </>
      )}
    </QueryGuard>
  )
}

function isHtml(text: string): boolean {
  return text.trimStart().startsWith('<!DOCTYPE') || text.trimStart().startsWith('<html')
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1 text-xs font-medium text-muted-foreground">{label}</h3>
      <div>{children}</div>
    </div>
  )
}
